import { Directive, ElementRef, HostListener, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Directive({
  selector: '[appPhoneFormat]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneFormatDirective),
      multi: true
    }
  ]
})
export class PhoneFormatDirective implements ControlValueAccessor {
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef<HTMLInputElement>) {}

  @HostListener('input', ['$event'])
  onInput(event: InputEvent): void {
    const input = this.el.nativeElement;
    const value = input.value;

    // Format the phone number
    const formatted = this.formatPhoneNumber(value);

    // Update the input value
    input.value = formatted;

    // Notify Angular of the change (raw digits only for form value)
    this.onChange(formatted.replace(/-/g, ''));
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Handle backspace - remove trailing dash if present
    if (event.key === 'Backspace') {
      const input = this.el.nativeElement;
      const value = input.value;

      // If the last character is a dash, remove it along with the previous digit
      if (value.endsWith('-')) {
        event.preventDefault();
        const newValue = value.slice(0, -1);
        input.value = newValue;
        this.onChange(newValue.replace(/-/g, ''));
      }
    }
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();

    // Trim trailing dash on blur
    const input = this.el.nativeElement;
    if (input.value.endsWith('-')) {
      input.value = input.value.slice(0, -1);
      this.onChange(input.value.replace(/-/g, ''));
    }
  }

  // Format phone number as XXX-XXX-XXXX
  private formatPhoneNumber(value: string): string {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Limit to 10 digits
    const limitedDigits = digits.slice(0, 10);

    // Format with dashes
    if (limitedDigits.length <= 3) {
      return limitedDigits;
    } else if (limitedDigits.length <= 6) {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
    } else {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    if (value) {
      this.el.nativeElement.value = this.formatPhoneNumber(value);
    } else {
      this.el.nativeElement.value = '';
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }
}
